import React, { useCallback, useEffect, useState } from "react";
import "./Template.css";

import TemplateFilter from "./TemplateFilter";
import TemplateTable from "./TemplateTable";
import TemplatePagination from "./TemplatePagination";
import { getDataDetail, getDataList } from "../../../../api/dataApi";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const getPagedArray = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.content)) return result.content;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.content)) return result.data.content;

  return [];
};

const normalizeItem = (item) => ({
  id: item.id || item.etcDocId || item.otherDocId || item.docId,
  title: item.title || "",
  writer: item.writer || item.userName || item.createdBy || "총학생회",
  date: formatDate(item.date || item.createdAt || item.updatedAt),
  rawDate: item.date || item.createdAt || item.updatedAt || "",
  view: item.viewer ?? item.view ?? 0,
  content:
    item.content ||
    item.contents ||
    item.description ||
    item.body ||
    "",
});

const sortLatestFirst = (a, b) => {
  const dateA = new Date(a.rawDate).getTime();
  const dateB = new Date(b.rawDate).getTime();

  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
    return dateB - dateA;
  }

  return Number(b.id || 0) - Number(a.id || 0);
};

const getDetailContent = async (id) => {
  if (!id) return "";

  try {
    const detail = await getDataDetail("etc", id);

    return (
      detail?.content ||
      detail?.contents ||
      detail?.description ||
      detail?.body ||
      ""
    );
  } catch (error) {
    console.error("기타 자료 상세 내용 조회 실패:", error);
    return "";
  }
};

export default function Template() {
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("title");
  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  const loadMissingContents = useCallback(async () => {
    const hasMissingContent = data.some((item) => item.id && !item.content);

    if (!hasMissingContent) return;

    try {
      setContentLoading(true);

      const nextData = await Promise.all(
        data.map(async (item) => {
          if (!item.id || item.content) return item;

          const detailContent = await getDetailContent(item.id);

          return {
            ...item,
            content: detailContent,
          };
        })
      );

      setData(nextData);
    } finally {
      setContentLoading(false);
    }
  }, [data]);

  const handleSearch = useCallback(async () => {
    if (searchType !== "title") {
      await loadMissingContents();
    }

    setSearchQuery(search);
    setPage(1);
  }, [search, searchType, loadMissingContents]);

  useEffect(() => {
    const fetchOthersList = async () => {
      try {
        setLoading(true);

        const result = await getDataList({
          category: "etc",
          searchCategory: "TITLE",
          keyWord: "",
          page: 0,
          size: 100,
        });

        const list = getPagedArray(result)
          .map(normalizeItem)
          .sort(sortLatestFirst);

        setData(list);
      } catch (error) {
        console.error("기타 자료 목록 조회 실패:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOthersList();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [search, searchType, handleSearch]);

  const filteredData = data.filter((item) => {
    const keyword = searchQuery.trim();

    const matchSearch = !keyword
      ? true
      : searchType === "title"
        ? item.title.includes(keyword)
        : item.content?.includes(keyword);

    return matchSearch;
  });

  const itemsPerPage = 10;
  const total = filteredData.length;
  const totalPages = Math.ceil(total / itemsPerPage);
  const safePage = total === 0 ? 1 : Math.min(page, totalPages || 1);

  useEffect(() => {
    if (total > 0 && page > totalPages) {
      setPage(1);
    }
  }, [total, page, totalPages]);

  const pagedData = filteredData.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  return (
    <div className="others-template">
      <div className="others-template-inner">
        <div className="others-template-header">
          학생자치활동에 필요한 기타 자료를 제공합니다.
        </div>

        <TemplateFilter
          search={search}
          setSearch={setSearch}
          searchType={searchType}
          setSearchType={setSearchType}
          handleSearch={handleSearch}
        />

        <div className="others-template-layout">
          <div className="others-template-content">
            {loading || contentLoading ? (
              <div className="others-template-empty">불러오는 중입니다.</div>
            ) : (
              <TemplateTable
                data={pagedData}
                page={safePage}
                itemsPerPage={itemsPerPage}
                total={total}
              />
            )}
          </div>

          {total > 0 && (
            <TemplatePagination
              page={safePage}
              setPage={setPage}
              total={total}
              itemsPerPage={itemsPerPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}